const CONTACT_INTENT_KEY = 'h4h_contact_intent';
const CONTACT_SOURCE_KEY = 'h4h_contact_source';
const CONTACT_TRIGGER_KEY = 'h4h_contact_trigger';
const QUOTE_KEYWORDS = [
    'quote',
    'pricing',
    'volume',
    'bulk',
    'bid',
    'room count',
    'property',
    'project',
];

const getSessionStorage = () => {
    try {
        return window.sessionStorage;
    } catch (error) {
        return null;
    }
};

const getCurrentPath = () => window.location.pathname || '';

const matchesQuoteIntent = (value = '') => {
    const normalized = String(value || '').toLowerCase();

    return QUOTE_KEYWORDS.some(keyword => normalized.includes(keyword));
};

const normalizeIntent = (intent = '') => String(intent || '').trim().toLowerCase();

const readStoredContactIntent = () => {
    const storage = getSessionStorage();

    if (!storage) {
        return {
            intent: '',
            sourcePath: '',
            trigger: '',
        };
    }

    return {
        intent: storage.getItem(CONTACT_INTENT_KEY) || '',
        sourcePath: storage.getItem(CONTACT_SOURCE_KEY) || '',
        trigger: storage.getItem(CONTACT_TRIGGER_KEY) || '',
    };
};

export const pushTrackedEvent = (eventName, payload = {}) => {
    if (!eventName) {
        return;
    }

    if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
            event: eventName,
            ...payload,
        });
    }

    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
    }

    document.dispatchEvent(new CustomEvent('h4h:tracking', {
        detail: {
            event: eventName,
            ...payload,
        },
    }));
};

export const rememberContactIntent = ({ intent = '', sourcePath = getCurrentPath(), trigger = '' } = {}) => {
    const storage = getSessionStorage();
    const normalizedIntent = normalizeIntent(intent);

    if (!storage || !normalizedIntent) {
        return;
    }

    storage.setItem(CONTACT_INTENT_KEY, normalizedIntent);
    storage.setItem(CONTACT_SOURCE_KEY, sourcePath || '');
    storage.setItem(CONTACT_TRIGGER_KEY, trigger || '');
};

export const inferContactIntent = ({
    explicitIntent = '',
    sourcePath = '',
    trigger = '',
    question = '',
} = {}) => {
    const normalizedIntent = normalizeIntent(explicitIntent);

    if (normalizedIntent === 'quote') {
        return 'quote';
    }

    if (normalizedIntent === 'contact') {
        return 'contact';
    }

    if (
        matchesQuoteIntent(sourcePath)
        || matchesQuoteIntent(trigger)
        || matchesQuoteIntent(question)
    ) {
        return 'quote';
    }

    return 'contact';
};

export const buildContactIntentTracking = ({ form, location = 'contact_page' } = {}) => {
    const storedIntent = readStoredContactIntent();
    const params = new URLSearchParams(window.location.search);
    const question = form?.querySelector('[name="contact_question"]')?.value || '';
    const company = form?.querySelector('[name="contact_companyname"]')?.value || '';
    const orderNumber = form?.querySelector('[name="contact_orderno"]')?.value || '';
    const trigger = params.get('trigger') || storedIntent.trigger || '';
    const sourcePath = storedIntent.sourcePath || document.referrer || '';
    const intent = inferContactIntent({
        explicitIntent: params.get('intent') || storedIntent.intent,
        sourcePath,
        trigger,
        question,
    });

    return {
        intent,
        eventName: intent === 'quote' ? 'quote_submit' : 'contact_submit',
        payload: {
            h4h_label: intent === 'quote' ? 'Quote Request' : 'Contact Request',
            h4h_location: location,
            h4h_destination: form?.getAttribute('action') || getCurrentPath(),
            h4h_intent: intent,
            h4h_source_path: sourcePath,
            h4h_trigger: trigger,
            h4h_has_company: company ? 'yes' : 'no',
            h4h_has_order_number: orderNumber ? 'yes' : 'no',
        },
    };
};

export const bindContactFormStartTracking = (form, location = 'contact_page') => {
    if (!form || form.dataset.h4hStartTrackingBound === 'true') {
        return;
    }

    const maybeTrackFormStart = (event) => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (!target.matches('input:not([type="hidden"]), textarea, select')) {
            return;
        }

        if (form.dataset.h4hStarted === 'true') {
            return;
        }

        const tracking = buildContactIntentTracking({ form, location });

        pushTrackedEvent(tracking.intent === 'quote' ? 'quote_start' : 'contact_start', {
            ...tracking.payload,
            h4h_label: tracking.intent === 'quote' ? 'Quote Form Start' : 'Contact Form Start',
        });

        form.dataset.h4hStarted = 'true';
    };

    form.addEventListener('focusin', maybeTrackFormStart);
    form.addEventListener('input', maybeTrackFormStart);
    form.dataset.h4hStartTrackingBound = 'true';
};

export const bindContactIntentLinkTracking = () => {
    if (document.body.dataset.h4hContactLinkTrackingBound === 'true') {
        return;
    }

    document.addEventListener('click', event => {
        const link = event.target.closest('a[href]');

        if (!link) {
            return;
        }

        const href = link.getAttribute('href') || '';

        if (!href.includes('/contact-us')) {
            return;
        }

        const label = link.dataset.h4hLabel || link.textContent.trim();
        const intent = inferContactIntent({
            explicitIntent: link.dataset.h4hIntent,
            sourcePath: href,
            trigger: label,
        });

        rememberContactIntent({
            intent,
            sourcePath: window.location.pathname,
            trigger: label,
        });

        pushTrackedEvent(intent === 'quote' ? 'quote_click' : 'contact_click', {
            h4h_label: label || (intent === 'quote' ? 'Quote Link' : 'Contact Link'),
            h4h_location: getCurrentPath(),
            h4h_destination: href,
            h4h_intent: intent,
        });
    });

    document.body.dataset.h4hContactLinkTrackingBound = 'true';
};

export const bindCheckoutStartTracking = () => {
    if (document.body.dataset.h4hCheckoutTrackingBound === 'true') {
        return;
    }

    document.addEventListener('click', event => {
        const link = event.target.closest('a[href]');

        if (!link) {
            return;
        }

        const href = link.getAttribute('href') || '';

        if (!href.includes('/checkout')) {
            return;
        }

        pushTrackedEvent('checkout_start', {
            h4h_label: link.textContent.trim() || 'Proceed to Checkout',
            h4h_location: getCurrentPath(),
            h4h_destination: href,
        });
    });

    document.body.dataset.h4hCheckoutTrackingBound = 'true';
};
